import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { format } from "date-fns";
import { History, Loader2, RefreshCw, RotateCcw } from "lucide-react";
import { certificateService } from "@/services/certificate/certificate.service";
import type { CertificateReissueBatch, IssuedCertificate } from "@/types/certificate.types";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const PAGE_SIZE = 20;

const formatDate = (value?: string | null, pattern = "MMM dd, yyyy"): string => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime()) ? "—" : format(date, pattern);
};

// What the confirm dialog is about to reissue.
type ReissueTarget = { kind: "selected"; ids: string[] } | { kind: "older"; count: number };

interface IssuedCertificatesPanelProps {
  templateId: string;
  /** Bumped by the parent after a save so the version summary refreshes. */
  refreshKey?: number;
}

const IssuedCertificatesPanel = ({ templateId, refreshKey = 0 }: IssuedCertificatesPanelProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [olderOnly, setOlderOnly] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState<ReissueTarget | null>(null);
  const [reason, setReason] = useState("");
  const [notifyApplicants, setNotifyApplicants] = useState(false);
  const [starting, setStarting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<IssuedCertificate | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const listKey = ["issued-certificates", templateId, page, olderOnly, search, refreshKey];
  const { data, isLoading, isError } = useQuery({
    queryKey: listKey,
    queryFn: () =>
      certificateService.getIssuedCertificates(templateId, { page, limit: PAGE_SIZE, olderOnly, search }),
  });

  // Pick up the most recent batch so progress survives a page reload.
  const { data: latestBatch } = useQuery({
    queryKey: ["certificate-reissue-latest", templateId],
    queryFn: () => certificateService.getLatestReissueBatch(templateId),
  });
  useEffect(() => {
    if (latestBatch && !batchId) setBatchId(latestBatch.id);
  }, [latestBatch, batchId]);

  const { data: batch } = useQuery({
    queryKey: ["certificate-reissue-batch", batchId],
    queryFn: () => certificateService.getReissueBatch(batchId!),
    enabled: !!batchId,
    refetchInterval: (query) =>
      (query.state.data as CertificateReissueBatch | undefined)?.status === "RUNNING" ? 2000 : false,
  });

  // Refresh the list once a batch this page watched finishes.
  const previousStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!batch) return;
    if (previousStatus.current === "RUNNING" && batch.status !== "RUNNING") {
      queryClient.invalidateQueries({ queryKey: ["issued-certificates", templateId] });
      const message = t("certificate.issuedCertificates.finished", { done: batch.done, failed: batch.failed });
      if (batch.failed > 0) toast.warning(message);
      else toast.success(message);
    }
    previousStatus.current = batch.status;
  }, [batch, queryClient, templateId, t]);

  const items = data?.items ?? [];
  const pages = data ? Math.max(1, Math.ceil(data.matching / PAGE_SIZE)) : 1;
  const running = batch?.status === "RUNNING";
  const allOnPageSelected = items.length > 0 && items.every((item) => selected.has(item.id));

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const togglePage = (checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      items.forEach((item) => (checked ? next.add(item.id) : next.delete(item.id)));
      return next;
    });
  };

  const openDialog = (next: ReissueTarget) => {
    setReason("");
    setNotifyApplicants(false);
    setTarget(next);
  };

  const handleStart = async () => {
    if (!target) return;
    setStarting(true);
    try {
      const started = await certificateService.startReissue(templateId, {
        ...(target.kind === "selected" ? { certificateIds: target.ids } : { olderVersionsOnly: true }),
        reason: reason.trim() || undefined,
        notifyApplicants,
      });
      previousStatus.current = "RUNNING";
      queryClient.setQueryData(["certificate-reissue-batch", started.id], started);
      setBatchId(started.id);
      setSelected(new Set());
      setTarget(null);
      toast.success(t("certificate.issuedCertificates.started"));
    } catch (error: any) {
      console.error("Failed to start certificate reissue", error);
      toast.error(error?.response?.data?.message || t("certificate.issuedCertificates.failedToStart"));
    } finally {
      setStarting(false);
    }
  };

  const handleRetry = async () => {
    if (!batch) return;
    setRetrying(true);
    try {
      const retried = await certificateService.retryReissueBatch(batch.id);
      previousStatus.current = "RUNNING";
      queryClient.setQueryData(["certificate-reissue-batch", batch.id], retried);
      toast.success(t("certificate.issuedCertificates.retryStarted"));
    } catch (error: any) {
      console.error("Failed to retry certificate reissue", error);
      toast.error(error?.response?.data?.message || t("certificate.issuedCertificates.failedToRetry"));
    } finally {
      setRetrying(false);
    }
  };

  const targetCount = target ? (target.kind === "selected" ? target.ids.length : target.count) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("certificate.issuedCertificates.title")}</CardTitle>
        <CardDescription>{t("certificate.issuedCertificates.description")}</CardDescription>
        {data && (
          <div className="flex flex-wrap items-center gap-3 pt-2 text-sm">
            <Badge variant="secondary">
              {t("certificate.issuedCertificates.currentVersion", { version: data.currentVersion })}
            </Badge>
            <span className="text-gray-600">
              {t("certificate.issuedCertificates.summary", { total: data.total, older: data.olderCount })}
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {batch && (
          <div className="rounded-md border p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 font-medium">
                {running && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("certificate.issuedCertificates.progressTitle", { version: batch.templateVersion })}
                <Badge variant={batch.status === "COMPLETED_WITH_ERRORS" ? "destructive" : "outline"}>
                  {batch.status === "RUNNING"
                    ? t("certificate.issuedCertificates.running")
                    : batch.status === "COMPLETED"
                      ? t("certificate.issuedCertificates.completed")
                      : t("certificate.issuedCertificates.completedWithErrors")}
                </Badge>
              </div>
              <span className="text-sm text-gray-600">{formatDate(batch.createdAt, "MMM dd, yyyy HH:mm")}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded bg-gray-100">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${batch.total ? ((batch.done + batch.failed) / batch.total) * 100 : 0}%` }}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span>{t("certificate.issuedCertificates.progress", { done: batch.done, total: batch.total })}</span>
              {batch.failed > 0 && (
                <span className="text-red-600">
                  {t("certificate.issuedCertificates.failedCount", { count: batch.failed })}
                </span>
              )}
              {!running && batch.failed > 0 && (
                <Button size="sm" variant="outline" onClick={handleRetry} disabled={retrying}>
                  <RotateCcw className="h-4 w-4 mr-1" />
                  {retrying ? t("certificate.issuedCertificates.retrying") : t("certificate.issuedCertificates.retryFailed")}
                </Button>
              )}
            </div>
            {batch.failures.length > 0 && (
              <ul className="max-h-40 overflow-y-auto text-sm text-red-700 space-y-1">
                {batch.failures.map((failure) => (
                  <li key={failure.certificateId}>
                    <span className="font-medium">{failure.certificateNumber}</span>: {failure.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("certificate.issuedCertificates.searchPlaceholder")}
            className="max-w-xs"
          />
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={olderOnly}
              onCheckedChange={(checked) => {
                setOlderOnly(checked);
                setPage(1);
              }}
            />
            {t("certificate.issuedCertificates.olderOnly")}
          </label>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={running || selected.size === 0}
              onClick={() => openDialog({ kind: "selected", ids: [...selected] })}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              {t("certificate.issuedCertificates.reissueSelected", { count: selected.size })}
            </Button>
            <Button
              disabled={running || !data?.olderCount}
              onClick={() => openDialog({ kind: "older", count: data?.olderCount ?? 0 })}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              {t("certificate.issuedCertificates.reissueAllOlder", { count: data?.olderCount ?? 0 })}
            </Button>
          </div>
        </div>

        {isError ? (
          <p className="text-sm text-red-600">{t("certificate.issuedCertificates.failedToLoad")}</p>
        ) : isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">{t("certificate.issuedCertificates.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-600">
                  <th className="py-2 pr-2 w-8">
                    <Checkbox checked={allOnPageSelected} onCheckedChange={(checked) => togglePage(!!checked)} />
                  </th>
                  <th className="py-2 pr-4">{t("certificate.issuedCertificates.number")}</th>
                  <th className="py-2 pr-4">{t("certificate.issuedCertificates.applicant")}</th>
                  <th className="py-2 pr-4">{t("certificate.issuedCertificates.version")}</th>
                  <th className="py-2 pr-4">{t("certificate.issuedCertificates.issued")}</th>
                  <th className="py-2 pr-4">{t("certificate.issuedCertificates.status")}</th>
                  <th className="py-2 pr-4">{t("certificate.issuedCertificates.reissued")}</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b last:border-0">
                    <td className="py-2 pr-2">
                      <Checkbox
                        checked={selected.has(item.id)}
                        onCheckedChange={(checked) => toggle(item.id, !!checked)}
                      />
                    </td>
                    <td className="py-2 pr-4 font-mono">{item.certificateNumber}</td>
                    <td className="py-2 pr-4">{item.applicantName}</td>
                    <td className="py-2 pr-4">
                      <span className="flex items-center gap-1">
                        v{item.templateVersion}
                        {data && item.templateVersion < data.currentVersion && (
                          <Badge variant="outline" className="text-amber-700 border-amber-300">
                            {t("certificate.issuedCertificates.older")}
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className="py-2 pr-4">{formatDate(item.issueDate)}</td>
                    <td className="py-2 pr-4">
                      {item.issuedAt
                        ? t("certificate.issuedCertificates.released")
                        : t("certificate.issuedCertificates.notReleased")}
                    </td>
                    <td className="py-2 pr-4">{formatDate(item.reissuedAt)}</td>
                    <td className="py-2 text-right">
                      {item.revisionCount > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => setHistoryFor(item)}>
                          <History className="h-4 w-4 mr-1" />
                          {t("certificate.issuedCertificates.history", { count: item.revisionCount })}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              {t("certificate.issuedCertificates.previous")}
            </Button>
            <span>{t("certificate.issuedCertificates.page", { page, pages })}</span>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              {t("certificate.issuedCertificates.next")}
            </Button>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!target} onOpenChange={(open) => { if (!open && !starting) setTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("certificate.issuedCertificates.dialogTitle", { count: targetCount })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("certificate.issuedCertificates.dialogDescription", { version: data?.currentVersion })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("certificate.issuedCertificates.reasonPlaceholder")}
            maxLength={500}
            disabled={starting}
          />
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={notifyApplicants}
              onCheckedChange={(checked) => setNotifyApplicants(!!checked)}
              disabled={starting}
              className="mt-0.5"
            />
            {t("certificate.issuedCertificates.notifyApplicants")}
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={starting}>{t("certificate.issuedCertificates.cancel")}</AlertDialogCancel>
            <Button onClick={handleStart} disabled={starting}>
              {starting ? t("certificate.issuedCertificates.starting") : t("certificate.issuedCertificates.confirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CertificateHistoryDialog certificate={historyFor} onClose={() => setHistoryFor(null)} />
    </Card>
  );
};

const CertificateHistoryDialog = ({
  certificate,
  onClose,
}: {
  certificate: IssuedCertificate | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["certificate-revisions", certificate?.id],
    queryFn: () => certificateService.getCertificateRevisions(certificate!.id),
    enabled: !!certificate,
  });

  const openRevision = async (revisionId: string) => {
    // Open the tab synchronously so popup blockers allow it, then load the PDF.
    const tab = window.open("", "_blank");
    try {
      const blob = await certificateService.getCertificateRevisionDocument(revisionId);
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      if (tab) tab.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      tab?.close();
      console.error("Failed to open certificate revision", error);
      toast.error(t("certificate.issuedCertificates.failedToOpen"));
    }
  };

  return (
    <Dialog open={!!certificate} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("certificate.issuedCertificates.historyTitle", { number: certificate?.certificateNumber })}
          </DialogTitle>
          <DialogDescription>
            {t("certificate.issuedCertificates.historyDescription", { version: certificate?.templateVersion })}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : !data?.revisions.length ? (
          <p className="text-sm text-gray-500">{t("certificate.issuedCertificates.noRevisions")}</p>
        ) : (
          <ul className="space-y-2">
            {data.revisions.map((revision) => (
              <li key={revision.id} className="flex items-start justify-between gap-3 rounded border p-3 text-sm">
                <div>
                  <div className="font-medium">
                    {t("certificate.issuedCertificates.replacedOn", {
                      version: revision.templateVersion,
                      date: formatDate(revision.replacedAt, "MMM dd, yyyy HH:mm"),
                    })}
                  </div>
                  {revision.reason && <div className="text-gray-600">{revision.reason}</div>}
                </div>
                <Button size="sm" variant="outline" onClick={() => openRevision(revision.id)}>
                  {t("certificate.issuedCertificates.view")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IssuedCertificatesPanel;
